import { billerBuilder, BillerModel } from 'app/biller/models/BillerModel';
import { BusinessUnitType } from 'app/business/services/useBusinessUnit';
import {
  domicilioBuilder,
  DomicilioModel
} from 'app/domicilio/models/DomicilioModel';
import moment, { Moment } from 'moment';
import { jsonToFormData } from 'shared/helpers/jsonToFormData';
import {
  vehiculoBuilder,
  VehiculoModel,
  vehiculoPartialBuilder,
  VehiculoPartialModel
} from '../../vehiculos/models/Vehiculo';
import { ChoferCategoria } from './ChoferCategoria';
import { ChoferConceptoNovedad } from './ChoferConceptoNovedad';
import { ChoferIdioma } from './ChoferIdioma';
import { ChoferMotivoDeshabilitacion } from './ChoferMotivoDeshabilitacion';

export interface ChoferPartialModel
  extends Pick<ChoferModel, 'id' | 'isDeleted'>,
    Pick<
      ChoferModel['general'],
      | 'codigo'
      | 'nombreCompleto'
      | 'categoria'
      | 'tienePosnet'
      | 'businessUnit'
      | 'phone'
    >,
    Pick<ChoferModel['personal'], 'foto'> {
  vehiculo: VehiculoPartialModel;
  // turnoDomicilio: ChoferModel['turno']/* ['domicilio'] */;
  billers: BillerModel[];
}

export interface ChoferModelSinTurno {
  id: number;
  general: ChoferGeneral;
  personal: ChoferPersonal;
  vencimientos: ChoferVencimiento;
  desvinculacion: ChoferDesvinculacion;
  preDesvinculacion: ChoferDesvinculacion;
  deshabilitaciones: ChoferDeshabilitacion[];
  isDeleted: boolean;
}

export interface ChoferModelFromBack {
  id: number;
  general: ChoferGeneral;
  personal: ChoferPersonal;
  turno: ChoferTurno | null;
  vencimientos: ChoferVencimiento;
  desvinculacion: ChoferDesvinculacion;
  preDesvinculacion: ChoferDesvinculacion;
  deshabilitaciones: ChoferDeshabilitacion[];
  isDeleted: boolean;
}

export interface ChoferModel {
  id: number;
  general: ChoferGeneral;
  personal: ChoferPersonal;
  turno: ChoferTurno;
  vencimientos: ChoferVencimiento;
  desvinculacion: ChoferDesvinculacion;
  preDesvinculacion: ChoferDesvinculacion;
  deshabilitaciones: ChoferDeshabilitacion[];
  isDeleted: boolean;
}



export interface ChoferGeneral {
  codigo: number;
  nombreCompleto: string;
  proveedores: BillerModel[];
  vehiculo: VehiculoModel;
  categoria: ChoferCategoria;
  tienePosnet: boolean;
  fechaIngreso: Moment;
  comision: number;
  businessUnit?: BusinessUnitType | null;
  phone?: string;
}

export interface ChoferPersonal {
  foto: string;
  fechaNacimiento: Moment;
  dni: number;
  dni_front_photo: string;
  dni_back_photo: string;
  cuit: number;
  domicilio: DomicilioModel;
  telefonoEmergencia: number | string;
  celular: number | string;
  mail: string;
  idiomas: ChoferIdioma[];
  tieneCursoDeDefensa: boolean;
}

export interface ChoferTurno {
  lunes: Turno[];
  martes: Turno[];
  miercoles: Turno[];
  jueves: Turno[];
  viernes: Turno[];
  sabado: Turno[];
  domingo: Turno[];
}

export interface ChoferDeshabilitacion {
  id?: number;
  fechaYHoraInicio: Moment;
  fechaYHoraFin: Moment;
  motivo: ChoferMotivoDeshabilitacion;
  observacion: string | null;
  finalizada: boolean;
  aceptada: boolean | null;
}

export interface ChoferNovedad {
  concepto: ChoferConceptoNovedad;
  fecha: Moment;
  monto: string;
}

export interface ChoferVencimiento {
  fechaLicencia: Moment;
  licence_front_photo: string;
  licence_back_photo: string;
  fotoLinti?: File | string;
  vencimientoLinti?: Moment;
  fotoExamenMedico?: File | string;
  vencimientoExamenMedico?: Moment;
  fotoLibretaSanitaria?: File | string;
  vencimientoLibretaSanitaria?: Moment;
}

export interface Turno {
  id: number;
  dia: string;                
  desde: Moment;              
  hasta: Moment;              
  id_conductor?: number;      
  creado_en: string;          
  actualizado_en: string;     
  direccion: DomicilioModel;       
}

export interface Direccion {  
  id: number;      
  calle: string;
  numero_calle: null;         
  piso: null;                 
  departamento: null;         
  localidad: null;            
  barrio: string;             
  provincia: string;          
  latitud: number;            
  longitud: number;           
  id_direccionable: number;   
  tipo_direccionable: string; 
  creado_en: string;          
  actualizado_en: string;     
  descripcion: null;          
}



export interface ChoferDesvinculacion {
  fecha: null | Moment;
  motivo: null | string;
  observacion: null | string;
}

export interface ChoferCreateModel
  extends Omit<
    ChoferModel,
    | 'desvinculacion'
    | 'preDesvinculacion'
    | 'deshabilitaciones'
    | 'isDeleted'
    | 'id'
    | 'general'
    | 'personal'
    | 'vencimientos'
    | 'turno'
  > {
  id?: number;
  general: Omit<ChoferGeneral, 'vehiculo'> & {
    vehiculo: VehiculoPartialModel;
  };
  personal: Omit<
    ChoferPersonal,
    'foto' | 'dni_front_photo' | 'dni_back_photo'
  > & {
    foto: File | string;
    dni_front_photo: File | string;
    dni_back_photo: File | string;
  };
  vencimientos: Omit<
    ChoferVencimiento,
    'licence_front_photo' | 'licence_back_photo'
  > & {
    licence_front_photo: File | string;
    licence_back_photo: File | string;
    fotoLinti?: File | string;
    vencimientoLinti?: Moment;
    fotoExamenMedico?: File | string;
    vencimientoExamenMedico?: Moment;
    fotoLibretaSanitaria?: File | string;
    vencimientoLibretaSanitaria?: Moment;
  };
}

export interface ChoferCRUD
  extends Omit<
    ChoferModel,
    | 'desvinculacion'
    | 'preDesvinculacion'
    | 'deshabilitaciones'
    | 'isDeleted'
    | 'id'
    | 'general'
    | 'personal'
    | 'vencimientos'
  > {
  id?: number;
  general: Omit<ChoferGeneral, 'vehiculo'> & {
    vehiculo: VehiculoPartialModel;
  };
  personal: Omit<
    ChoferPersonal,
    'foto' | 'dni_front_photo' | 'dni_back_photo'
  > & {
    foto: File | string;
    dni_front_photo: File | string;
    dni_back_photo: File | string;
  };
  vencimientos: Omit<
    ChoferVencimiento,
    'licence_front_photo' | 'licence_back_photo'
  > & {
    licence_front_photo: File | string;
    licence_back_photo: File | string;
    fotoLinti?: File | string;
    vencimientoLinti?: Moment;
    fotoExamenMedico?: File | string;
    vencimientoExamenMedico?: Moment;
    fotoLibretaSanitaria?: File | string;
    vencimientoLibretaSanitaria?: Moment;
  };
}

export interface TurnoPayload {
  day: string;
  start_hour: number;
  start_minute: number;
  end_hour: number;
  end_minute: number;
  address: {
    street: string;
    street_number: number;
    floor?: string;
    apartment?: string;
    locality: string;
    district: string;
    province: string;
    latitude: number;
    longitude: number;
  };
}

const horariosBuilder = {
  fromBackEnd: (day: Record<keyof ChoferTurno, any[]>): ChoferTurno => {
  const horarios: ChoferTurno = {
    lunes: [],
    martes: [],
    miercoles: [],
    jueves: [],
    viernes: [],
    sabado: [], 
    domingo: [],
  };

    const dayMap: Record<string, keyof ChoferTurno> = {
      monday: 'lunes',
      tuesday: 'martes',
      wednesday: 'miercoles',
      thursday: 'jueves',
      friday: 'viernes',
      saturday: 'sabado',
      sunday: 'domingo'
    };

    Object.keys(day).forEach(englishDay => {
      const dayKey = dayMap[englishDay];
      const daySchedules = day[englishDay];

      if (dayKey && Array.isArray(daySchedules)) {
        daySchedules.forEach(schedule => {
          const turno: Turno = {
            id: schedule.id,
            dia: dayKey, 
            desde: moment(`${schedule.start_hour}:${schedule.start_minute}`, 'HH:mm'),
            hasta: moment(`${schedule.end_hour}:${schedule.end_minute}`, 'HH:mm'),
            id_conductor: schedule.driver_id,
            creado_en: schedule.created_at,
            actualizado_en: schedule.updated_at,
            direccion: domicilioBuilder.fromBackEnd(schedule.address),
          };

          horarios[dayKey].push(turno);
        });
      }
    });

    return horarios;
  },
  toBackEnd: (horarios: Omit<ChoferTurno, 'domicilio'>) => {
/*     const mapHorario = (day: string) => (horario: Turno) => ({
      day,
      from_schedule: horario.desde.format('HH:mm'),
      to_schedule: horario.hasta.format('HH:mm')
    });
    return [
      ...horarios.lunes.map(mapHorario('monday')),
      ...horarios.horariosMartes.map(mapHorario('tuesday')),
      ...horarios.horariosMiercoles.map(mapHorario('wednesday')),
      ...horarios.horariosJueves.map(mapHorario('thursday')),
      ...horarios.horariosViernes.map(mapHorario('friday')),
      ...horarios.horariosSabado.map(mapHorario('saturday')),
      ...horarios.horariosDomingo.map(mapHorario('sunday'))
    ]; */
  }
};

export const choferBuilder = {
  fromBackEnd: (_chofer): ChoferModelFromBack => {
    const { personalInfo, ...chofer } = _chofer;
    return {
      id: chofer.id,
      general: {
        codigo: chofer.code,
        nombreCompleto: chofer.name,
        vehiculo: vehiculoBuilder.fromBackEnd(chofer.vehicle),
        categoria: chofer.category,
        tienePosnet: chofer.hasPosnet,
        fechaIngreso: moment(personalInfo.admissionDate),
        comision: personalInfo.commission,
        proveedores: chofer.billers.map(billerBuilder.fromBackEnd),
        businessUnit: chofer.business_units
      },
      personal: {
        foto: personalInfo.photo,
        dni_front_photo: personalInfo.dni_front_photo,
        dni_back_photo: personalInfo.dni_back_photo,
        fechaNacimiento: moment(personalInfo.birthdate),
        dni: personalInfo.dni,
        cuit: personalInfo.cuit,
        telefonoEmergencia: personalInfo.phone,
        celular: personalInfo.cellphone,
        mail: personalInfo.email,
        tieneCursoDeDefensa: !!personalInfo.hasDefensiveCourse,
        idiomas: chofer.languages.map(({ name }) => name),
        domicilio: domicilioBuilder.fromBackEnd(chofer.address)
      },
      desvinculacion: {
        fecha: chofer.eliminationDate,
        motivo: chofer.eliminationReason,
        observacion: chofer.eliminationObservation
      },
      preDesvinculacion: {
        fecha: chofer.disassociateDate,
        motivo: chofer.disassociateReason,
        observacion: chofer.disassociateObservation
      },
      turno: chofer.shifts?{
        ...horariosBuilder.fromBackEnd(chofer.shifts)
      }: null,
      vencimientos: {
        licence_front_photo: chofer.licence_front_photo,
        licence_back_photo: chofer.licence_back_photo,
        fechaLicencia: moment(chofer.licenceExpirationDate),
        ...(chofer?.linti
          ? {
              fotoLinti: chofer.linti.photo,
              vencimientoLinti: moment(chofer.linti.expiration_date)
            }
          : {}),
        ...(chofer?.medical_examination
          ? {
              fotoExamenMedico: chofer.medical_examination.photo,
              vencimientoExamenMedico: moment(
                chofer.medical_examination.expiration_date
              )
            }
          : {}),
        ...(chofer?.health_record
          ? {
              fotoLibretaSanitaria: chofer.health_record.photo,
              vencimientoLibretaSanitaria: moment(
                chofer.health_record.expiration_date
              )
            }
          : {})
      },
      deshabilitaciones: [],
      isDeleted: chofer.isDeleted
    };
  },
  createdfromBackEnd: (_chofer): ChoferModelSinTurno => {
    const { personalInfo, ...chofer } = _chofer;
    return {
      id: chofer.id,
      general: {
        codigo: chofer.code,
        nombreCompleto: chofer.name,
        vehiculo: vehiculoBuilder.fromBackEnd(chofer.vehicle),
        categoria: chofer.category,
        tienePosnet: chofer.hasPosnet,
        fechaIngreso: moment(personalInfo.admissionDate),
        comision: personalInfo.commission,
        proveedores: chofer.billers.map(billerBuilder.fromBackEnd),
        businessUnit: chofer.business_units
      },
      personal: {
        foto: personalInfo.photo,
        dni_front_photo: personalInfo.dni_front_photo,
        dni_back_photo: personalInfo.dni_back_photo,
        fechaNacimiento: moment(personalInfo.birthdate),
        dni: personalInfo.dni,
        cuit: personalInfo.cuit,
        telefonoEmergencia: personalInfo.phone,
        celular: personalInfo.cellphone,
        mail: personalInfo.email,
        tieneCursoDeDefensa: !!personalInfo.hasDefensiveCourse,
        idiomas: chofer.languages.map(({ name }) => name),
        domicilio: domicilioBuilder.fromBackEnd(chofer.address)
      },
      desvinculacion: {
        fecha: chofer.eliminationDate,
        motivo: chofer.eliminationReason,
        observacion: chofer.eliminationObservation
      },
      preDesvinculacion: {
        fecha: chofer.disassociateDate,
        motivo: chofer.disassociateReason,
        observacion: chofer.disassociateObservation
      },
      vencimientos: {
        licence_front_photo: chofer.licence_front_photo,
        licence_back_photo: chofer.licence_back_photo,
        fechaLicencia: moment(chofer.licenceExpirationDate),
        ...(chofer?.linti?.expirationDate
          ? {
              fotoLinti: chofer.linti.photo,
              vencimientoLinti: moment(chofer.linti.expirationDate)
            }
          : {}),
        ...(chofer?.medical_examination
          ? {
              fotoExamenMedico: chofer.medical_examination.photo,
              vencimientoExamenMedico: moment(
                chofer.medical_examination.expirationDate
              )
            }
          : {}),
        ...(chofer?.health_record
          ? {
              fotoLibretaSanitaria: chofer.health_record.photo,
              vencimientoLibretaSanitaria: moment(
                chofer.health_record.expirationDate
              )
            }
          : {})
      },
      deshabilitaciones: [],
      isDeleted: chofer.isDeleted
    };
  },
  toCreateBackEnd: (chofer: ChoferCreateModel) => {
    const value = {
      code: chofer.general.codigo,
      name: chofer.general.nombreCompleto,
      vehicle_id: chofer.general.vehiculo.id,
      biller_ids: chofer.general.proveedores.map(({ id }) => id),
      category: chofer.general.categoria,
      has_posnet: chofer.general.tienePosnet,
      business_units_id: chofer.general.businessUnit?.id,
      personal_info: {
        photo: chofer.personal.foto,
        dni_front_photo: chofer.personal.dni_front_photo,
        dni_back_photo: chofer.personal.dni_back_photo,
        birthdate: chofer.personal.fechaNacimiento.format('YYYY-MM-DD'),
        dni: chofer.personal.dni,
        cuit: chofer.personal.cuit,
        address: domicilioBuilder.toBackEnd(chofer.personal.domicilio),
        phone: chofer.personal.telefonoEmergencia,
        cellphone: chofer.personal.celular,
        email: chofer.personal.mail,
        admission_date: chofer.general.fechaIngreso.format('YYYY-MM-DD'),
        commission: chofer.general.comision,
        has_defensive_course: chofer.personal.tieneCursoDeDefensa,
        languages: chofer.personal.idiomas
      },
      licence_expiration_date:
        chofer.vencimientos.fechaLicencia.format('YYYY-MM-DD'),
      licence_front_photo: chofer.vencimientos.licence_front_photo,
      licence_back_photo: chofer.vencimientos.licence_back_photo,
      ...(chofer.vencimientos.vencimientoLinti && chofer.vencimientos.fotoLinti
        ? {
            linti: {
              photo: chofer.vencimientos.fotoLinti,
              expiration_date:
                chofer.vencimientos.vencimientoLinti.format('YYYY-MM-DD')
            }
          }
        : {}),
      ...(chofer.vencimientos.vencimientoExamenMedico &&
      chofer.vencimientos.fotoExamenMedico
        ? {
            medical_examination: {
              photo: chofer.vencimientos.fotoExamenMedico,
              expiration_date:
                chofer.vencimientos.vencimientoExamenMedico.format('YYYY-MM-DD')
            }
          }
        : {}),
      ...(chofer.vencimientos.vencimientoLibretaSanitaria &&
      chofer.vencimientos.fotoLibretaSanitaria
        ? {
            health_record: {
              photo: chofer.vencimientos.fotoLibretaSanitaria,
              expiration_date:
                chofer.vencimientos.vencimientoLibretaSanitaria.format(
                  'YYYY-MM-DD'
                )
            }
          }
        : {})
    };
    return jsonToFormData(value);
  },
  toBackEnd: (chofer: ChoferCRUD) => {
    const value = {
      code: chofer.general.codigo,
      name: chofer.general.nombreCompleto,
      vehicle_id: chofer.general.vehiculo.id,
      biller_ids: chofer.general.proveedores.map(({ id }) => id),
      category: chofer.general.categoria,
      has_posnet: chofer.general.tienePosnet,
      business_units_id: chofer.general.businessUnit?.id,
      personal_info: {
        photo: chofer.personal.foto,
        dni_front_photo: chofer.personal.dni_front_photo,
        dni_back_photo: chofer.personal.dni_back_photo,
        birthdate: chofer.personal.fechaNacimiento.format('YYYY-MM-DD'),
        dni: chofer.personal.dni,
        cuit: chofer.personal.cuit,
        address: domicilioBuilder.toBackEnd(chofer.personal.domicilio),
        phone: chofer.personal.telefonoEmergencia,
        cellphone: chofer.personal.celular,
        email: chofer.personal.mail,
        admission_date: chofer.general.fechaIngreso.format('YYYY-MM-DD'),
        commission: chofer.general.comision,
        has_defensive_course: chofer.personal.tieneCursoDeDefensa,
        languages: chofer.personal.idiomas
      },
      licence_expiration_date:
        chofer.vencimientos.fechaLicencia.format('YYYY-MM-DD'),
      licence_front_photo: chofer.vencimientos.licence_front_photo,
      licence_back_photo: chofer.vencimientos.licence_back_photo,
      ...(chofer.vencimientos.vencimientoLinti && chofer.vencimientos.fotoLinti
        ? {
            linti: {
              photo: chofer.vencimientos.fotoLinti,
              expiration_date:
                chofer.vencimientos.vencimientoLinti.format('YYYY-MM-DD')
            }
          }
        : {}),
      ...(chofer.vencimientos.vencimientoExamenMedico &&
      chofer.vencimientos.fotoExamenMedico
        ? {
            medical_examination: {
              photo: chofer.vencimientos.fotoExamenMedico,
              expiration_date:
                chofer.vencimientos.vencimientoExamenMedico.format('YYYY-MM-DD')
            }
          }
        : {}),
      ...(chofer.vencimientos.vencimientoLibretaSanitaria &&
      chofer.vencimientos.fotoLibretaSanitaria
        ? {
            health_record: {
              photo: chofer.vencimientos.fotoLibretaSanitaria,
              expiration_date:
                chofer.vencimientos.vencimientoLibretaSanitaria.format(
                  'YYYY-MM-DD'
                )
            }
          }
        : {})
    };
    return jsonToFormData(value);
  }
};

export const choferPartialBuilder = {
  fromBackEnd: (chofer): ChoferPartialModel => {
    return {
      id: chofer.id,
      codigo: chofer.code,
      nombreCompleto: chofer.name,
      phone: chofer.phone,
      foto: chofer.photo,
      vehiculo: vehiculoPartialBuilder.fromBackEnd(chofer.vehicle),
      // turnoDomicilio: domicilioBuilder.fromBackEnd(chofer.shift_address),
      categoria: chofer.category,
      isDeleted: chofer.isDeleted,
      tienePosnet: chofer.hasPosnet,
      billers: chofer.billers.map(billerBuilder.fromBackEnd),
      businessUnit: chofer.business_units
    };
  }
};
